import { useEffect, useState } from "react";
import { X, ShieldCheck, Star, Lock, Phone, Mail, MapPin, ExternalLink, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";

// iter103 — Referral welcome splash for Angi + Thumbtack customers.
// Shown ONCE per browser session when we detect a marketplace referral.
// Purpose: build trust before the customer sees a booking form. Explains
// the free portal, reassures no account is created before they act.
const REFERRAL_KEY = "jdsn_referral_welcome_ack";

// URL params or referrer patterns that indicate a marketplace referral.
function detectReferralSource() {
  const params = new URLSearchParams(window.location.search);
  const explicit = (params.get("ref") || params.get("src")
    || params.get("utm_source") || params.get("source") || "").toLowerCase();
  if (explicit === "angi" || explicit === "angies" || explicit === "angies_list") return "angi";
  if (explicit === "thumbtack" || explicit === "tt") return "thumbtack";
  const r = (document.referrer || "").toLowerCase();
  if (r.includes("angi.com") || r.includes("angieslist.com")) return "angi";
  if (r.includes("thumbtack.com")) return "thumbtack";
  return null;
}

const SOURCE_META = {
  angi: {
    label: "Angi",
    tagline: "You were referred from Angi",
    color: "bg-orange-100 text-orange-800 border-orange-300",
  },
  thumbtack: {
    label: "Thumbtack",
    tagline: "You were referred from Thumbtack",
    color: "bg-cyan-100 text-cyan-800 border-cyan-300",
  },
};

export default function ReferralWelcomeSplash() {
  const [source, setSource] = useState(null);
  const [open, setOpen] = useState(false);
  const [learnMore, setLearnMore] = useState(false);

  useEffect(() => {
    try {
      const acked = sessionStorage.getItem(REFERRAL_KEY);
      if (acked) return;
      const src = detectReferralSource();
      if (!src) return;
      setSource(src);
      setOpen(true);
      // Stash for the booking submission so backend can log the origin.
      sessionStorage.setItem("jdsn_referral_source", src);
    } catch {
      /* sessionStorage disabled — skip splash */
    }
  }, []);

  const dismiss = () => {
    try { sessionStorage.setItem(REFERRAL_KEY, "1"); } catch {
      /* ignore */
    }
    setOpen(false);
  };

  if (!open || !source) return null;
  const meta = SOURCE_META[source];

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="referral-welcome-splash"
    >
      <div className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom">
        {/* Header — brand strip */}
        <div className="relative px-6 pt-6 pb-4 bg-gradient-to-br from-[#062818] to-[#047857] text-white">
          <button
            onClick={dismiss}
            aria-label="Close"
            className="absolute top-3 right-3 p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2">
            <img
              src="/icons/jdsn-logo.png"
              alt="JDSN Lawn Care"
              className="w-10 h-10 rounded-full bg-white p-1"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-emerald-200">JDSN Lawn Care</div>
              <div className="text-[14px] font-extrabold">Jacksonville, FL · Since 2018</div>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full border ${meta.color}`}>
              <ExternalLink className="w-3 h-3" /> {meta.tagline}
            </span>
          </div>
        </div>

        {/* Message */}
        <div className="px-6 py-5 space-y-3">
          <h2 className="text-xl font-extrabold text-gray-900 tracking-tight font-[Outfit]">
            Welcome! You&apos;re on the official JDSN Lawn Care website.
          </h2>
          <p className="text-[13.5px] text-gray-700 leading-relaxed">
            You were referred by <b>{meta.label}</b> to JDSN Lawn Care. To help
            you manage your quote, appointments, invoices, payments, and
            service history, we provide a <b>free and secure customer portal</b>.
          </p>
          <p className="text-[13.5px] text-gray-700 leading-relaxed">
            Your account will only be created after you continue with your
            booking. <b>No payment is required</b> just to see a quote.
          </p>

          {/* Trust chips */}
          <div className="pt-2 grid grid-cols-2 gap-2">
            <TrustChip icon={ShieldCheck} label="Licensed & Insured" />
            <TrustChip icon={Star}        label="5.0 · Google Reviews" />
            <TrustChip icon={Lock}        label="SSL Secure" />
            <TrustChip icon={CheckCircle2} label="500+ jobs completed" />
          </div>

          {/* Business contact */}
          <div className="pt-2 flex items-center gap-3 flex-wrap text-[12px] text-gray-600">
            <a href="tel:9048328730" className="inline-flex items-center gap-1 hover:text-[#047857] font-bold">
              <Phone className="w-3.5 h-3.5" /> (904) 832-8730
            </a>
            <a href="mailto:jdsnlawncare@gmail.com" className="inline-flex items-center gap-1 hover:text-[#047857] font-bold">
              <Mail className="w-3.5 h-3.5" /> jdsnlawncare@gmail.com
            </a>
            <span className="inline-flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" /> Jacksonville, FL
            </span>
          </div>

          {learnMore && (
            <div className="mt-2 rounded-2xl border border-gray-200 bg-gray-50 p-4 text-[12.5px] text-gray-700 leading-relaxed">
              <b>What is the customer portal?</b><br/>
              A free, secure area where you can view your appointments,
              estimates, invoices, and payments — and message our team
              directly. Your account is created automatically only when you
              submit your booking, so we can send you your invoice and job
              updates. Your information is protected and will never be shared.
              You can request account deletion at any time.
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 pt-2 flex items-center gap-2">
          <Button
            variant="outline"
            className="rounded-full flex-1 font-extrabold"
            onClick={() => setLearnMore(!learnMore)}
            data-testid="referral-splash-learn-more"
          >
            {learnMore ? "Hide details" : "Learn More"}
          </Button>
          <Button
            className="rounded-full flex-1 bg-[#047857] hover:bg-[#036349] text-white font-extrabold"
            onClick={dismiss}
            data-testid="referral-splash-continue"
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
}

function TrustChip({ icon: Icon, label }) {
  return (
    <div className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-[11px] font-extrabold text-gray-700">
      <Icon className="w-3.5 h-3.5 text-[#047857]" /> {label}
    </div>
  );
}
